import { useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import metrics from '@/common/services/metrics';
const paramName = 'authModalType'; // A hook for controlling the authentication modal state through URL parameters.
//
// Example:
// const { isOpen, authType, openModal, closeModal, changeAuthType } = useAuthModal();
//
// // Open login modal
// openModal('login');
//
// // Close modal
// closeModal();
//
// // Switch between auth types
// changeAuthType('signup');

const useAuthModal = () => {
  const router = useRouter();
  const searchParams = useSearchParams(); // Get current auth type from URL

  const authModalType = searchParams.get(paramName); // Check if modal is open

  const isOpen = !!authModalType; // Create new URLSearchParams with all current query parameters

  const createParams = useCallback(() => {
    const params = new URLSearchParams();
    searchParams.forEach((value, key) => {
      params.set(key, value);
    });
    return params;
  }, [searchParams]); // Open modal with specific auth type

  const openModal = useCallback(type => {
    // Track event when user opens signup modal
    if (type === 'signup') {
      metrics.sendEvent('chart_builder_signup_start', {}, {
        maxEventsPerSession: 1
      });
    }

    const params = createParams();
    params.set(paramName, type);
    router.push({
      pathname: router.pathname,
      search: params.toString()
    }, undefined, {
      shallow: true
    });
  }, [createParams, router]); // Close modal by removing auth type parameter

  const closeModal = useCallback(() => {
    const params = createParams();
    params.delete(paramName);
    const search = params.toString();
    router.push({
      pathname: router.pathname,
      search: search ? `?${search}` : ''
    }, undefined, {
      shallow: true
    });
  }, [createParams, router]); // Change auth type

  const changeAuthType = useCallback(type => {
    openModal(type);
  }, [openModal]);
  return {
    isOpen,
    authModalType,
    openModal,
    closeModal,
    changeAuthType
  };
};

export default useAuthModal;